import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { LoadingButton } from '../ui/LoadingSpinner';
import { apiClient, setLogoutCallback } from '../../utils/apiClient';
import { logout, selectCurrentUser, selectCurrentToken } from '../../features/auth/authSlice';
import { useToast } from '../../contexts/ToastContext';
import BenefitPreview from './BenefitPreview';
import BackButton from '../ui/BackButton';
import ClaimErrorBoundary from '../ClaimErrorBoundary';
import { canShowPayoutPreview, validateClaimAgainstUserStory } from '../../utils/userStoryValidator';
import { FeatureFlagContext } from '../../utils/featureFlags';
import ConfirmationDialog from '../ui/ConfirmationDialog';
import claimCauseService from '../../services/claimCauseService';
import ErrorMessage from '../ClaimsList/ErrorMessage';
import LoadingSpinner from '../ui/LoadingSpinner';
import OmForm from '../ui/OmForm';
import OmTextField from '../ui/OmTextField';
import OmSelect from '../ui/OmSelect';
import OmSelectOption from '../ui/OmSelectOption';
import OmDatePicker from '../ui/OmDatePicker';
import OmCheckbox from '../ui/OmCheckbox';
import OmCircularProgress from '../ui/OmCircularProgress';
import businessTypeService from '../../services/businessTypeService';
import { BUSINESS_TYPE_FIELD_MAPPINGS } from '../../constants/businessTypeFieldMappings';
// Phase 2: Import conditional field services for dynamic field management
import { conditionalFieldService } from '../../services/conditionalFieldService';
import { fieldMetadataService } from '../../services/fieldMetadataService';
import { schemaValidationService } from '../../services/schemaValidationService';
// Phase 2: Import Dynamic Field Renderer for schema-driven field rendering
import DynamicFieldRenderer from './DynamicFieldRenderer';
// Import Step Components
import Step1ClaimMetadata from './steps/Step1ClaimMetadata';
import Step2ClaimantInformation from './steps/Step2ClaimantInformation';
import Step3ClaimDetails from './steps/Step3ClaimDetails';
import Step4DocumentUpload from './steps/Step4DocumentUpload';
import Step5ClaimSummary from './steps/Step5ClaimSummary';
// Import Bank Service for bank code resolution
import bankService from '../../services/bankService';

const DynamicClaimForm = () => {
  // Redux state
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning } = useToast(); // Phase 1: Critical Fix - Destructure toast methods
  const user = useSelector(selectCurrentUser);
  const token = useSelector(selectCurrentToken);
  
  // Setup logout callback for apiClient
  useEffect(() => {
    setLogoutCallback(() => {
      
      dispatch(logout());
    });
  }, [dispatch]);
  
  // State management
  const [currentStep, setCurrentStep] = useState(1);
  const [schema, setSchema] = useState(null);
  const [formData, setFormData] = useState({
    dateFiled: new Date().toISOString().split('T')[0] // Auto-populate with today's date
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [schemaLoading, setSchemaLoading] = useState(true);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [submitWithoutDocs, setSubmitWithoutDocs] = useState(false);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [claimCauses, setClaimCauses] = useState([]);
  
  // Phase 2: Enhanced state management for dynamic fields
  const [visibleFields, setVisibleFields] = useState([]);
  const [fieldMetadata, setFieldMetadata] = useState({});
  
  const [validationDialog, setValidationDialog] = useState({ isOpen: false, title: '', message: '', details: [] });
  const [submitErrorDialog, setSubmitErrorDialog] = useState({ isOpen: false, title: '', message: '' });

  // Load schema and officers on component mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setSchemaLoading(true);
        
        // Check if user is authenticated first
        if (!user || !token) {
          throw new Error('Authentication required. Please log in to access this form.');
        }
        
        // Fetch business claim types from businessTypeService
        try {
          const businessTypesData = await businessTypeService.fetchBusinessClaimTypes();
          setBusinessTypes(businessTypesData);
        } catch (businessTypesError) {
          console.error('Failed to load business types:', businessTypesError.message);
          setBusinessTypes([]);
        }

        // Fetch claim causes from claimCauseService
        try {
          const claimCausesData = await claimCauseService.getClaimCauses();
          setClaimCauses(claimCausesData);
        } catch (claimCausesError) {
          console.error('Failed to load claim causes:', claimCausesError.message);
          setClaimCauses([]);
        }
        
        // Fetch schema using apiClient
        const schemaResponse = await apiClient.get('claims/schema');

        // Validate the schema response structure
        if (!schemaResponse || !schemaResponse.data) {
          throw new Error('Invalid schema response: missing data property');
        }

        // Handle the double-wrapped data structure from formatSuccessResponse
        let schemaData = schemaResponse.data;
        
        // If the response is double-wrapped (data.data), unwrap it
        if (schemaData.data && typeof schemaData.data === 'object') {
          schemaData = schemaData.data;
        }
        
        // Validate critical schema structure
        if (!schemaData.baseFields) {
          throw new Error('Invalid schema response: missing baseFields property');
        }
        
        if (!schemaData.baseFields.policyNumber) {
          throw new Error('Invalid schema response: missing critical field policyNumber in baseFields');
        }

        setSchema(schemaData);

        // Initialize form data with defaults
        setFormData({
          dateFiled: new Date().toISOString().split('T')[0],
          type: '', // Backend expects 'type' field with business claim codes: DTH, 010, 012, 013, 002, PTD, 005, TTD
          policyNumber: '',
          claimantName: '',
          claimantMobile: '',
          claimantEmail: '',
          claimantAddress: '',
          bankAccountName: '',
          bankName: '',
          accountNumber: '',
          branch: '',
          
          assignedToId: null, // Will be assigned later via pull model
          // TODO: Remove frontend sumAssured once policy lookup is available - DEV ONLY
          sumAssured: '',
          // DEV ONLY: Add isActive field for eligibility testing
          isActive: true,
          // Add incident date field for eligibility validation
          incidentDate: '',
          // Event date - when the incident/event occurred
          eventDate: '',
          // Initialize illness stage as null - will be set only for cancer conditions
          illnessStage: null
        });


      } catch (error) {
        // Enhanced error handling with specific error categorization
        console.error('❌ DynamicClaimForm: Error occurred while loading form schema:', error);
        
        // More specific error messages based on error type
        let errorMessage = 'Failed to load form schema. ';
        
        if (error.message.includes('Authentication required')) {
          errorMessage = 'Please log in to access the claim form.';
          // Optionally redirect to login
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
        } else if (error.message.includes('missing baseFields') || error.message.includes('missing critical field')) {
          errorMessage = 'The claim form configuration is incomplete. Please contact technical support.';
        } else if (error.message.includes('403') || error.message.includes('Access denied')) {
          errorMessage = 'You do not have permission to access this form. Please contact your administrator.';
        } else if (error.message.includes('401') || error.message.includes('token')) {
          errorMessage = 'Your session has expired. Please log in again.';
          // Clear invalid token and logout user
          dispatch(logout());
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
        } else if (error.message.includes('Failed to fetch') || error.message.includes('fetch')) {
          errorMessage = 'Cannot connect to the server. Please check if the backend server is running on port 3000.';
        } else if (error.message.includes('Unexpected token') || error.message.includes('Invalid schema response')) {
          errorMessage = 'The server returned an invalid response. Please check the API endpoint configuration.';
        } else {
          errorMessage += 'Please refresh the page and try again.';
        }
        
        setErrors({ general: errorMessage });
      } finally {
        
        setSchemaLoading(false);
      }
    };

    // Add a safety timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      
      setSchemaLoading(false);
      setErrors({ general: 'Loading timed out. Please refresh the page and try again.' });
    }, 10000); // 10 second timeout

    loadInitialData().finally(() => {
      clearTimeout(timeoutId);
    });
  }, [user, token, dispatch]);

  // Phase 2: Effect to update visible fields when form data changes
  useEffect(() => {
    if (!schema || !formData.type) return;
    
    // Debounce rapid field visibility changes to prevent component mounting race conditions
    const timeoutId = setTimeout(() => {
      const newVisibleFields = conditionalFieldService.getVisibleFields(
        schema, 
        formData.type, 
        formData
      );
      
      // DEBUG: Log visible fields for type 013
      if (formData.type === '013') {
      }
      
      // Only update if fields actually changed
      if (JSON.stringify(newVisibleFields) !== JSON.stringify(visibleFields)) {
        setVisibleFields(newVisibleFields);
        
        // Clean up form data for fields that are no longer visible
        const cleanedFormData = { ...formData };
        Object.keys(formData).forEach(field => {
          if (!newVisibleFields.includes(field) && field !== 'type') {
            delete cleanedFormData[field];
          }
        });
        
        // Update form data if cleanup occurred
        if (Object.keys(cleanedFormData).length !== Object.keys(formData).length) {
          setFormData(cleanedFormData);
        }
      }
      
      // Update field metadata for all visible fields
      const newFieldMetadata = {};
      newVisibleFields.forEach(fieldName => {
        const metadata = fieldMetadataService.getFieldMetadata(
          fieldName, 
          formData.type, 
          schema
        );
        
        // DEBUG: Log metadata generation for parentRelationshipVerified
        if (fieldName === 'parentRelationshipVerified') {
        }
        
        newFieldMetadata[fieldName] = metadata;
      });
      setFieldMetadata(newFieldMetadata);
    }, 100); // 100ms debounce to prevent rapid field visibility changes
    
    return () => clearTimeout(timeoutId);
  }, [formData.type, formData.disabilityType, schema]);

  // Removed: validateAgainstNIA function - Ghana Card verification removed
  // Removed: handleFieldBlur function - Ghana Card verification removed
  const handleFieldBlur = (fieldName, value) => {
    // Placeholder function - Ghana Card validation removed
  };

  // Phase 3: Enhanced input change handler with validation
  const handleInputChangeWithValidation = (fieldName, value) => {
    // Log input changes for type field
    if (fieldName === 'type') {
    }
    
    // Update form data
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));

    // Clear form validation error when field is changed
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }

    // If claim type changes, clear type-specific fields and auto-set disability type
    if (fieldName === 'type') {
      const clearedTypeData = { ...formData };
      
      // Clear all type-specific fields from previous selections
      Object.keys(schema?.typeSpecificFields || {}).forEach(type => {
        Object.keys(schema.typeSpecificFields[type]).forEach(field => {
          delete clearedTypeData[field];
        });
      });

      // Explicitly clear illness stage for non-critical illness claims
      if (value !== '002') {
        clearedTypeData.illnessStage = null;
      }

      // Auto-set disability type based on claim type (to match backend behavior)
      let autoSetData = {
        ...clearedTypeData,
        [fieldName]: value
      };

      if (value === '004') {
        autoSetData.disabilityType = 'PTD';
      } else if (value === '006') {
        autoSetData.disabilityType = 'TTD';
      }

      // Initialize boolean fields for spousal cover claims
      if (value === '010') {
        autoSetData.spouseRelationshipVerified = false;
        autoSetData.marriageCertificateProvided = false;
      }

      // Initialize boolean fields for coffin cover claims
      if (value === '012') {
        autoSetData.funeralReceiptsProvided = false;
      }

      // Initialize boolean fields for parent cover claims
      if (value === '013') {
        autoSetData.parentRelationshipVerified = false;
      }

      setFormData(autoSetData);
    }
  };

  const sanitizeEnumValue = (fieldName, value) => {
    if (!value) return value;
       
    // Enum value mappings to ensure backend compatibility
    const enumMappings = {
      conditionSeverityStage: {
        'early': 'EARLY',
        'intermediate': 'INTERMEDIATE', 
        'advanced': 'ADVANCED',
        'terminal': 'TERMINAL',
        'Early': 'EARLY',
        'Intermediate': 'INTERMEDIATE',
        'Advanced': 'ADVANCED', 
        'Terminal': 'TERMINAL'
      },
      disabilityType: {
        'temporary': 'TTD',
        'permanent': 'PTD',
        'Temporary': 'TTD',
        'Permanent': 'PTD',
        'TEMPORARY': 'TTD',
        'PERMANENT': 'PTD'
      },
      illnessStage: {
        'Stage I': 'I',
        'Stage II': 'II', 
        'Stage III': 'III',
        'Stage IV': 'IV',
        'Other Stage': 'OTHER',
        '1': 'I',
        '2': 'II',
        '3': 'III',
        '4': 'IV'
      },
      type: {
        'death': 'DTH',
        'disability': 'PTD', 
        'critical_illness': '002',
        'Death': 'DTH',
        'Disability': 'PTD',
        'Critical Illness': '002',
        'spousal_cover': '010',
        'coffin_cover': '012', 
        'parent_cover': '013',
        'medical': '005',
        'temporary_disability': 'TTD',
        'permanent_disability': 'PTD'
      }
    };

    // Apply mapping if it exists, otherwise return uppercase
    if (enumMappings[fieldName] && enumMappings[fieldName][value]) {
      const mappedValue = enumMappings[fieldName][value];
      if (fieldName === 'type') {
      }
      return mappedValue;
    }
    
    // Default to uppercase for any enum-like field
    if (typeof value === 'string' && ['conditionSeverityStage', 'disabilityType', 'type', 'illnessStage'].includes(fieldName)) {
      const uppercaseValue = value.toUpperCase();
      if (fieldName === 'type') {
      }
      return uppercaseValue;
    }
    
    if (fieldName === 'type') {
    }
    
    return value;
  };

  // Helper function to cast values to expected data types
  const castToExpectedType = (fieldName, value, fieldDef) => {
    if (value === null || value === undefined || value === '') return value;
    
    switch (fieldDef?.type) {
      case 'number':
        return Number(value);
      case 'boolean':
        return value === true || value === 'true' || value === '1';
      case 'date':
        // Ensure date is in ISO format for backend
        return new Date(value).toISOString().split('T')[0];
      default:
        return value;
    }
  };

  // Enhanced validate current step with user story compliance
  const validateStep = async (step) => {
    const newErrors = {};
    
    if (!schema) return false;

    // Phase 3: Check for NIA validation errors before proceeding
    // COMMENTED OUT FOR TESTING - Ghana Card validation bypass
    /*
    const currentValidationErrors = Object.keys(validationErrors);
    
    if (currentValidationErrors.length > 0) {
      setError('Please correct the highlighted validation errors before proceeding. Your information must match your verified Ghana Card details.');
      return false;
    }
    */

    switch (step) {
      case 1: // Claim Metadata
        if (!formData.policyNumber?.trim()) {
          newErrors.policyNumber = 'Policy number is required';
        }
        if (!formData.type) {
          newErrors.type = 'Claim type is required';
        }
        // TODO: Remove sumAssured validation once policy lookup is available - DEV ONLY
        if (formData.sumAssured === undefined || formData.sumAssured === null || isNaN(parseFloat(formData.sumAssured)) || parseFloat(formData.sumAssured) < 0) {
          newErrors.sumAssured = 'Sum assured is required and must be 0 or greater';
        }
        
        // DEV ONLY: Validate isActive field
        if (formData.isActive === null || formData.isActive === undefined) {
          newErrors.isActive = 'Active status is required';
        }
        break;

      case 2: // Claimant Information
        // Check for required fields based on actual schema
        if (schema.baseFields?.claimantName?.required && !formData.claimantName?.trim()) {
          newErrors.claimantName = 'Claimant name is required';
        }
        // Validate phone number format if provided
        if (formData.claimantMobile && !isValidPhoneNumber(formData.claimantMobile)) {
          newErrors.claimantMobile = 'Include country code (e.g. +233242XXXXXXX)';
        }
        // Validate email format if provided
        if (formData.claimantEmail && !isValidEmail(formData.claimantEmail)) {
          newErrors.claimantEmail = 'Must be a valid email, e.g. user@example.com';
        }
        // Validate bank details if any are provided
        if (formData.accountNumber && !formData.bankName) {
          newErrors.bankName = 'Bank name is required when account number is provided';
        }
        if (formData.bankName && !formData.accountNumber) {
          newErrors.accountNumber = 'Account number is required when bank name is provided';
        }
        // Validate bank account number format if provided
        if (formData.accountNumber && !isValidBankAccount(formData.accountNumber)) {
          newErrors.accountNumber = '8–20 digits, numbers only';
        }
        break;

      case 3: // Type-Specific Details with Backend-Aligned Validation
        if (formData.type) {
          // DEATH CLAIMS - Backend Required Fields & Business Rules
          if (formData.type === 'DTH') {
            // Required fields for death claims
            if (!formData.deceasedName?.trim()) {
              newErrors.deceasedName = 'Deceased name is required for death claims';
            }
            if (!formData.relationshipToDeceased?.trim()) {
              newErrors.relationshipToDeceased = 'Relationship to deceased is required for death claims';
            }
            if (!formData.deceasedDateOfDeath) {
              newErrors.deceasedDateOfDeath = 'Date of death is required for death claims';
            }
            if (!formData.causeOfDeath?.trim()) {
              newErrors.causeOfDeath = 'Cause of death is required for death claims';
            }
            if (!formData.burialCosts || parseFloat(formData.burialCosts) < 0) {
              newErrors.burialCosts = 'Burial costs are required and must be a positive number';
            }

            // Business rule: burialDate >= dateOfDeath
            if (formData.burialDate && formData.deceasedDateOfDeath) {
              const burialDate = new Date(formData.burialDate);
              const deathDate = new Date(formData.deceasedDateOfDeath);
              
              if (!isNaN(burialDate.getTime()) && !isNaN(deathDate.getTime())) {
                if (burialDate < deathDate) {
                  newErrors.burialDate = 'Burial date cannot be before date of death';
                }
              }
            }

            // Date validation: death date cannot be in future
            if (formData.deceasedDateOfDeath) {
              const deathDate = new Date(formData.deceasedDateOfDeath);
              if (!isNaN(deathDate.getTime()) && deathDate > new Date()) {
                newErrors.deceasedDateOfDeath = 'Date of death cannot be in the future';
              }
            }
          }

          // DISABILITY CLAIMS - Backend Required Fields & Business Rules  
          if (formData.type === 'PTD' || formData.type === 'TTD') {
            // Required fields for disability claims
            if (!formData.dateOfInjury) {
              newErrors.dateOfInjury = 'Date of injury is required for disability claims';
            }
            if (!formData.diagnosisDate) {
              newErrors.diagnosisDate = 'Diagnosis date is required for disability claims';
            }
            if (!formData.disabilityType) {
              newErrors.disabilityType = 'Disability type is required for disability claims';
            }

            // Business rule: diagnosisDate - injuryDate <= 365 days
            if (formData.dateOfInjury && formData.diagnosisDate) {
              const injuryDate = new Date(formData.dateOfInjury);
              const diagnosisDate = new Date(formData.diagnosisDate);
              
              if (!isNaN(injuryDate.getTime()) && !isNaN(diagnosisDate.getTime())) {
                const daysDiff = (diagnosisDate - injuryDate) / (1000 * 60 * 60 * 24);
                if (daysDiff > 365) {
                  newErrors.diagnosisDate = 'Diagnosis date must be within 365 days of injury date';
                }

                // Business rule: If gap > 30 days, employment status must be confirmed
                if (daysDiff > 30 && !formData.employmentStatusConfirmed) {
                  newErrors.employmentStatusConfirmed = 'Employment status must be confirmed for claims submitted more than 30 days after injury';
                }
              }
            }

            // Date validation: injury date cannot be in future
            if (formData.dateOfInjury) {
              const injuryDate = new Date(formData.dateOfInjury);
              if (!isNaN(injuryDate.getTime()) && injuryDate > new Date()) {
                newErrors.dateOfInjury = 'Date of injury cannot be in the future';
              }
            }
          }

          // CRITICAL ILLNESS CLAIMS - Backend Required Fields & Business Rules
          if (formData.type === '002') {
            // Required fields for critical illness claims
            if (!formData.diagnosisDate) {
              newErrors.diagnosisDate = 'Diagnosis date is required for critical illness claims';
            }
            if (!formData.conditionSeverityStage) {
              newErrors.conditionSeverityStage = 'Severity stage is required for critical illness claims';
            }
            // Payout percentage will be calculated by claims officers during approval
            if (formData.treatmentRequired === null || formData.treatmentRequired === undefined) {
              newErrors.treatmentRequired = 'Treatment required status is required for critical illness claims';
            }

            // Illness stage validation - only required for cancer-related diagnoses
            const isCancerRelated = formData.diagnosis && 
              formData.diagnosis.toLowerCase().includes('cancer');
            
            if (isCancerRelated && !formData.illnessStage) {
              newErrors.illnessStage = 'Illness stage is required for cancer-related diagnoses';
            }

            // Payout percentage validation removed - will be handled by claims officers

            // Date validation: diagnosis date cannot be in future
            if (formData.diagnosisDate) {
              const diagnosisDate = new Date(formData.diagnosisDate);
              if (!isNaN(diagnosisDate.getTime()) && diagnosisDate > new Date()) {
                newErrors.diagnosisDate = 'Diagnosis date cannot be in the future';
              }
            }

            // Cancer-specific validation: illness stage required for cancer diagnoses
            if (formData.diagnosis && formData.diagnosis.toLowerCase().includes('cancer')) {
              if (!formData.illnessStage) {
                newErrors.illnessStage = 'Illness stage is required for cancer-related conditions';
              }
            }
          }

          // TTD duration validation (user story compliance)
          if (formData.disabilityType === 'TTD' && formData.estimatedPaymentDuration) {
            const duration = parseInt(formData.estimatedPaymentDuration);
            if (duration > 6) {
              newErrors.estimatedPaymentDuration = 'TTD claims cannot exceed 6 months duration per user story';
            }
          }
        }
        
        // Try user story validation if we have enough data
        try {
          if (FeatureFlagContext.requiresStrictValidation()) {
            const validation = await validateClaimAgainstUserStory(formData);
            if (!validation.isValid) {
              validation.errors.forEach(error => {
                newErrors.userStoryCompliance = newErrors.userStoryCompliance || [];
                newErrors.userStoryCompliance.push(error);
              });
            }
          }
        } catch (error) {
          console.warn('User story validation failed:', error);
        }
        break;

      case 4: // Document Upload and Review
        // Get required documents for this claim type from schema
        const requiredDocs = schema?.requiredDocuments?.[formData.type] || [];
        
        if (requiredDocs.length > 0) {
          // Get uploaded document types (excluding OTHER)
          const uploadedDocTypes = uploadedFiles
            .filter(f => {
              const docType = f.documentType || f.type;
              return docType && docType !== 'OTHER';
            })
            .map(f => f.documentType || f.type);
          
          // Check if all required documents are uploaded
          const missingDocs = requiredDocs.filter(doc => !uploadedDocTypes.includes(doc));
          
          if (missingDocs.length > 0) {
            // Get human-readable labels for missing documents
            const missingLabels = missingDocs.map(docType => {
              const docTypeDef = schema?.documentTypes?.find(dt => dt.value === docType);
              return docTypeDef ? docTypeDef.label : docType;
            });
            
            newErrors.documents = `Missing required documents: ${missingLabels.join(', ')}. Please upload all required documents to proceed.`;
          }
        } else if (uploadedFiles.length === 0 && !submitWithoutDocs) {
          // No required docs defined, but at least upload something (unless explicitly skipping)
          newErrors.documents = 'Please upload at least one supporting document or choose to finish without documents';
        }
        break;

      case 5: // Claim Summary Review
        // Minimal validation - just ensure we have basic claim data
        if (!formData.type) {
          newErrors.type = 'Claim type is required';
        }
        if (!formData.policyNumber?.trim()) {
          newErrors.policyNumber = 'Policy number is required';
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to get fields for each step
  const getFieldsForStep = (step) => {
    switch (step) {
      case 1: // Claim Metadata
        return ['type', 'policyNumber', 'dateFiled', 'sumAssured', 'isActive'];
      case 2: // Claimant Information
        return ['claimantName', 'claimantMobile', 'claimantEmail', 'claimantDateOfBirth', 'claimantAddress', 'accountNumber', 'bankAccountName', 'bankName', 'branch'];
      case 3: // Type-Specific Details
        if (!formData.type || !schema) return [];
        const typeSpecificFieldNames = Object.keys(schema.typeSpecificFields?.[formData.type] || {});
        return typeSpecificFieldNames;
      case 4: // Documents
        return ['documents'];
      case 5: // Claim Summary Review
        return []; // No specific fields to validate - just review
      default:
        return [];
    }
  };

  // Replace static validation with schema-based validation
  const validateStepWithSchema = (step) => {
    
    // DEBUG: Check specifically for parentRelationshipVerified
    
    if (!schema) {
      return false;
    }
    
    // Use schema-based validation instead of hardcoded logic
    let stepErrors = schemaValidationService.validateStep(step, formData, schema, getFieldsForStep);
    
    // Step 4: Custom document validation
    if (step === 4) {
      
      const requiredDocs = schema?.requiredDocuments?.[formData.type] || [];
      
      // Filter uploaded files to get document types (handle both 'type' and 'documentType' fields)
      const uploadedDocTypes = uploadedFiles
        .map(file => file.documentType || file.type)
        .filter(Boolean);
      
      // Check which required docs are missing
      const missingDocs = requiredDocs.filter(docType => !uploadedDocTypes.includes(docType));
      
      if (missingDocs.length > 0) {
        stepErrors = {
          ...stepErrors,
          documents: `Missing required documents: ${missingDocs.join(', ')}. Please upload all required documents to proceed.`
        };
      } else {
      }
    }
    
    
    setErrors(stepErrors);
    const isValid = Object.keys(stepErrors).length === 0;
    
    
    return isValid;
  };

  // Replace isFieldRequired function with schema-based version
  const isFieldRequired = (fieldName) => {
    return schemaValidationService.isFieldRequired(fieldName, formData, schema);
  };

  // Add real-time field validation
  const handleInputChangeWithSchemaValidation = (fieldName, value) => {
    // DEBUG: Log all changes for parentRelationshipVerified
    if (fieldName === 'parentRelationshipVerified') {
    }
    
    // Special handling for diagnosis field in critical illness claims
    if (fieldName === 'diagnosis' && formData.type === '002') {
      const isCancerRelated = value && value.toLowerCase().includes('cancer');
      
      setFormData(prev => ({
        ...prev,
        [fieldName]: value,
        // Clear illness stage if diagnosis is not cancer-related
        illnessStage: isCancerRelated ? prev.illnessStage : null
      }));
    } else {
      setFormData(prev => ({ ...prev, [fieldName]: value }));
    }
    
    // Clear existing form errors
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }

    // Clear NIA validation error when user starts typing
    // REMOVED - validationErrors no longer exists after Ghana Card removal
    /*
    if (validationErrors[fieldName]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
    */

    // Real-time validation for current field
    if (schema) {
      const fieldError = schemaValidationService.validateField(
        fieldName, 
        value, 
        { ...formData, [fieldName]: value }, 
        schema
      );
      
      if (fieldError) {
        setErrors(prev => ({
          ...prev,
          [fieldName]: fieldError
        }));
      }
    }

    // If claim type changes, clear type-specific fields and auto-set disability type
    if (fieldName === 'type') {
      const clearedTypeData = { ...formData };
      
      // Clear all type-specific fields from previous selections
      Object.keys(schema?.typeSpecificFields || {}).forEach(type => {
        Object.keys(schema.typeSpecificFields[type]).forEach(field => {
          delete clearedTypeData[field];
        });
      });

      // Explicitly clear illness stage for non-critical illness claims
      if (value !== '002') {
        clearedTypeData.illnessStage = null;
      }

      // Auto-set disability type based on claim type (to match backend behavior)
      let autoSetData = {
        ...clearedTypeData,
        [fieldName]: value
      };

      if (value === '004') {
        autoSetData.disabilityType = 'PTD';
      } else if (value === '006') {
        autoSetData.disabilityType = 'TTD';
      }

      // Initialize boolean fields for spousal cover claims
      if (value === '010') {
        autoSetData.spouseRelationshipVerified = false;
        autoSetData.marriageCertificateProvided = false;
      }

      // Initialize boolean fields for coffin cover claims
      if (value === '012') {
        autoSetData.funeralReceiptsProvided = false;
      }

      // Initialize boolean fields for parent cover claims
      if (value === '013') {
        autoSetData.parentRelationshipVerified = false;
      }

      setFormData(autoSetData);
    }
  };

  // Legacy handleInputChange that will be replaced
  const handleInputChange = handleInputChangeWithSchemaValidation;

  // Helper function to get field-specific help text based on backend business rules
  const getFieldHelpText = (fieldName, claimType = formData.type) => {
    const helpTexts = {
      'burialDate': 'Must be the same or after the Date of Death. Format: YYYY-MM-DD.',
      'employmentStatusConfirmed': 'Required if claiming more than 30 days after injury.',
      'payoutPercentage': 'Payout percentage will be calculated by claims officers during approval based on condition type and severity.',
      'diagnosisDate': claimType === 'DISABILITY' ? 'Must be within 365 days of injury date. Format: YYYY-MM-DD.' : 'Format: YYYY-MM-DD.',
      'dateOfInjury': 'Format: YYYY-MM-DD.',
      'deceasedDateOfDeath': 'Format: YYYY-MM-DD.',
      'deceasedDateOfBirth': 'Format: YYYY-MM-DD.',
      'lastWorkingDate': 'Format: YYYY-MM-DD.',
      'paymentStartDate': 'Format: YYYY-MM-DD.',
      'paymentEndDate': 'Format: YYYY-MM-DD.',
      'claimantMobile': 'Include country code (e.g. +233242XXXXXXX).',
      'claimantEmail': 'Must be a valid email, e.g. user@example.com.',
      'accountNumber': '8–20 digits, numbers only.',
      'policyNumber': 'Required',
      'claimantName': 'Required',
      'type': 'Required'
    };

    return helpTexts[fieldName] || '';
  };

  // Step navigation handlers
  const handleNextStep = async () => {
    // Try schema validation first, fallback to legacy validation if schema not available
    const isValid = schema 
      ? validateStepWithSchema(currentStep)
      : await validateStep(currentStep);
    
    if (isValid) {
      // If moving from Step 1 to Step 2, fetch member details for pre-filling
      if (currentStep === 1) {
        const updatedFormData = await fetchMemberDetails();
        
        // Block if fetchMemberDetails returned null (policy/staff number not found - 404 error)
        if (updatedFormData === null) {
          console.warn('🚫 Policy/Staff validation failed (404), blocking Step 2');
          return; // Block navigation
        }
        
        // Business Rule: Block inactive users from proceeding to Step 2
        // Use the updated form data directly instead of relying on state
        if (updatedFormData && updatedFormData.isActive === false) {
          setErrors(prev => ({
            ...prev,
            general: 'This policy is not active. Claim submission not allowed for inactive policies.'
          }));
          console.warn('🚫 SLAMS: Blocking inactive user from Step 2. Policy status:', updatedFormData.isActive);
          return; // Block navigation to Step 2
        }
        
      }
      setCurrentStep(prev => prev + 1);
    } else {
      try {
        // Use the errors state that was already set by validateStepWithSchema
        // This includes our custom Step 4 document validation
        const stepErrors = errors;
        const errorEntries = Object.entries(stepErrors);
        const details = errorEntries.slice(0, 8).map(([field, message]) => ({ label: field, value: message }));
        setValidationDialog({
          isOpen: true,
          title: 'Please fix the highlighted fields',
          message: `We found ${errorEntries.length} issue${errorEntries.length === 1 ? '' : 's'} on this step. Correct them to continue.`,
          details
        });
      } catch (e) {
        setValidationDialog({
          isOpen: true,
          title: 'Validation error',
          message: 'Some required information is missing or invalid. Please review the form and try again.',
          details: []
        });
      }
    }
  };

  // Fetch member details from API when transitioning from Step 1 to Step 2
  const fetchMemberDetails = async () => {
    
    // Only proceed if we have the required fields
    if (!formData.policyNumber || !formData.memberStaffNumber) {
      console.error('❌ fetchMemberDetails: Missing required fields for member details API call:', {
        policyNumber: formData.policyNumber,
        memberStaffNumber: formData.memberStaffNumber,
        policyNumberExists: !!formData.policyNumber,
        memberStaffNumberExists: !!formData.memberStaffNumber,
        policyNumberTruthy: formData.policyNumber ? 'truthy' : 'falsy',
        memberStaffNumberTruthy: formData.memberStaffNumber ? 'truthy' : 'falsy'
      });
      return;
    }

    
    try {
      setIsLoading(true);
      
      const apiParams = {
        scheme_no: formData.policyNumber,
        staff_no: formData.memberStaffNumber
      };
      
      
      
      const response = await apiClient.get('claims/member-details', {
        params: apiParams
      });

      // Handle triple-wrapped response structure from backend
      // Backend wraps SLAMS response: { data: { data: { success: true, data: { member_details: {...} } } } }
      let memberDetailsData = null;
      
      if (response.data && response.data.data && response.data.data.data && response.data.data.data.member_details) {
        // Triple-wrapped structure: response.data.data.data.member_details
        memberDetailsData = response.data.data.data.member_details;
      } else if (response.data && response.data.data && response.data.data.member_details) {
        // Double-wrapped structure: response.data.data.member_details
        memberDetailsData = response.data.data.member_details;
      } else if (response.data && response.data.member_details) {
        // Single-wrapped structure: response.data.member_details
        memberDetailsData = response.data.member_details;
      } else {
        throw new Error('Invalid response structure: member_details not found in expected locations');
      }

      if (memberDetailsData) {
        
        // Pre-fill Step 2 fields with member details
        const updatedFormData = { ...formData };
        // Clear any stale banking fields before applying new SLAMS data to avoid persistence across attempts
        updatedFormData.bankName = '';
        updatedFormData.bankCode = '';
        updatedFormData.bankBranch = '';
        updatedFormData.branch = '';
        updatedFormData.accountNumber = '';
        updatedFormData.bankAccountName = '';
        
        // Extract SLAMS financial data for business rules
        if (memberDetailsData.financial_information?.total_sum_assured !== undefined) {
          updatedFormData.sumAssured = memberDetailsData.financial_information.total_sum_assured;
        }
        
        if (memberDetailsData.scheme_information?.scheme_is_active !== undefined) {
          updatedFormData.isActive = memberDetailsData.scheme_information.scheme_is_active === 1;
        }
        
        // Map member details to form fields using nested SLAMS structure
        
        // Extract name from basic_information.full_name
        if (memberDetailsData.basic_information?.full_name) {
          updatedFormData.claimantName = memberDetailsData.basic_information.full_name;
        } else {
        }
        
        // Extract phone from contact_information.mobile_number
        if (memberDetailsData.contact_information?.mobile_number) {
          updatedFormData.claimantMobile = memberDetailsData.contact_information.mobile_number;
        } else {
        }
        
        // Extract email from contact_information.email
        if (memberDetailsData.contact_information?.email) {
          updatedFormData.claimantEmail = memberDetailsData.contact_information.email;
        } else {
        }
        
        // Extract date of birth from basic_information.date_of_birth
        if (memberDetailsData.basic_information?.date_of_birth) {
          // Convert SLAMS date format "1985-11-19 00:00:00" to YYYY-MM-DD
          const dobString = memberDetailsData.basic_information.date_of_birth;
          const dobDate = dobString.split(' ')[0]; // Extract date part only
          updatedFormData.claimantDateOfBirth = dobDate;
        } else {
        }
        
        // Extract address from contact_information.address
        if (memberDetailsData.contact_information?.address) {
          updatedFormData.claimantAddress = memberDetailsData.contact_information.address;
        } else {
        }
        
        // Banking details from banking_details section with detailed logging
        
        if (memberDetailsData.banking_details) {
          const bankDetails = memberDetailsData.banking_details;
          
          // Map bank account number from bank_account
          if (bankDetails.bank_account) {
            updatedFormData.accountNumber = bankDetails.bank_account;
          } else {
          }
          
          // Map bank branch from bank_branch_id  
          if (bankDetails.bank_branch_id) {
            updatedFormData.branch = bankDetails.bank_branch_id;
          } else {
          }
          
          // Map bank code from bank_code and resolve to bank name
          if (bankDetails.bank_code) {
            updatedFormData.bankCode = bankDetails.bank_code;
            
            // Resolve bank code to readable bank name using bank service
            try {
              const bankName = await bankService.resolveBankName(bankDetails.bank_code);
              if (bankName && bankName !== bankDetails.bank_code) {
                updatedFormData.bankName = bankName;
              } else {
                updatedFormData.bankName = bankDetails.bank_code;
              }

              // Find and auto-select the matching bank branch option
              const matchingOption = await bankService.findBankBranchOption(
                bankDetails.bank_code, 
                bankDetails.bank_branch_id
              );
              
              if (matchingOption) {
                updatedFormData.bankBranch = matchingOption.value;
              } else {
              }
              
            } catch (bankServiceError) {
              console.error('❌ Bank service failed to resolve bank code:', {
                bankCode: bankDetails.bank_code,
                error: bankServiceError.message
              });
              // Fallback to using bank code as bank name
              updatedFormData.bankName = bankDetails.bank_code;
            }
          } else {
          }
          
          // Use claimant name as bank account name if not available separately
          if (updatedFormData.claimantName && !updatedFormData.bankAccountName) {
            updatedFormData.bankAccountName = updatedFormData.claimantName;
          }
        } else {
        }


        setFormData(updatedFormData);
        
        
        return updatedFormData;
      } else {
        // This should not be reached since we handle the structure check above
        console.error('❌ Unexpected: memberDetailsData is null after structure validation');
        return formData; // Return current form data
      }
    } catch (error) {
      console.error('❌ fetchMemberDetails: API call failed with error:', {
        error: error,
        errorMessage: error.message,
        errorCode: error.code,
        errorStatus: error.status,
        errorResponse: error.response,
        errorResponseData: error.response?.data,
        errorResponseStatus: error.response?.status,
        errorResponseStatusText: error.response?.statusText,
        errorStack: error.stack,
        apiParams: {
          scheme_no: formData.policyNumber,
          staff_no: formData.memberStaffNumber
        },
        formDataAtError: formData
      });
      
      // Check if policy/staff number doesn't exist (404 errors)
      // Error code is at top level (error.code) after handleApiError processing
      const errorCode = error.code || error.errorCode || error.response?.data?.code;
      const httpStatus = error.httpStatus || error.response?.status;
      
      if (httpStatus === 404) {
        if (errorCode === 'SCHEME_NOT_FOUND') {
          setErrors(prev => ({
            ...prev,
            policyNumber: 'Policy number does not exist in the system'
          }));
          showError('Policy number not found. Please verify and try again.');
          return null; // Return null to block progression
        }
        
        if (errorCode === 'MEMBER_NOT_FOUND') {
          setErrors(prev => ({
            ...prev,
            memberStaffNumber: 'Staff number does not exist for this policy'
          }));
          showError('Staff number not found. Please verify and try again.');
          return null; // Return null to block progression
        }
      }
      
      // For other errors (503 service unavailable, network errors), allow manual entry
      showError('Unable to auto-fill member details. Please enter them manually.');
      return formData; // Return current form data for non-404 errors
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  // Handle editing specific steps from the summary
  const handleEdit = (stepNumber) => {
    setCurrentStep(stepNumber);
  };

  // File upload handlers
  // File upload wrapper for Step4 component
  const handleFileUploadForStep4 = (validFiles) => {
    setUploadedFiles(prev => [...prev, ...validFiles]);
  };

  // Auto-tick boolean fields based on document uploads for spousal cover claims
  useEffect(() => {
    if (formData.type === '010' && uploadedFiles.length > 0) {
      const uploadedDocumentTypes = uploadedFiles.map(file => file.documentType);
      
      setFormData(prevFormData => {
        const updatedFormData = { ...prevFormData };
        
        // Auto-tick marriageCertificateProvided if MARRIAGE_CERTIFICATE is uploaded
        if (uploadedDocumentTypes.includes('MARRIAGE_CERTIFICATE')) {
          updatedFormData.marriageCertificateProvided = true;
        }
        
        // Auto-tick spouseRelationshipVerified if RELATIONSHIP_DECLARATION is uploaded
        if (uploadedDocumentTypes.includes('RELATIONSHIP_DECLARATION')) {
          updatedFormData.spouseRelationshipVerified = true;
        }
        
        return updatedFormData;
      });
    }
  }, [uploadedFiles, formData.type]);

  // Auto-tick boolean fields based on document uploads for coffin cover claims
  useEffect(() => {
    if (formData.type === '012' && uploadedFiles.length > 0) {
      const uploadedDocumentTypes = uploadedFiles.map(file => file.documentType);
      
      setFormData(prevFormData => {
        const updatedFormData = { ...prevFormData };
        
        // Auto-tick funeralReceiptsProvided if FUNERAL_RECEIPTS is uploaded
        if (uploadedDocumentTypes.includes('FUNERAL_RECEIPTS')) {
          updatedFormData.funeralReceiptsProvided = true;
        }
        
        return updatedFormData;
      });
    }
  }, [uploadedFiles, formData.type]);

  // Auto-tick boolean fields based on document uploads for parent cover claims
  useEffect(() => {
    if (formData.type === '013' && uploadedFiles.length > 0) {
      const uploadedDocumentTypes = uploadedFiles.map(file => file.documentType);
      
      setFormData(prevFormData => {
        const updatedFormData = { ...prevFormData };
        
        // Auto-tick parentRelationshipVerified if RELATIONSHIP_DECLARATION is uploaded
        if (uploadedDocumentTypes.includes('RELATIONSHIP_DECLARATION')) {
          updatedFormData.parentRelationshipVerified = true;
        }
        
        return updatedFormData;
      });
    }
  }, [uploadedFiles, formData.type]);

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    
    // Validate file types and sizes
    const validFiles = files.filter(file => {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const maxSize = 10 * 1024 * 1024; // 10MB
      
      if (!validTypes.includes(file.type)) {
        showError(`File ${file.name} has an invalid type. Please upload PDF, JPG, PNG, DOC, or DOCX files.`);
        return false;
      }
      
      if (file.size > maxSize) {
        showError(`File ${file.name} is too large. Maximum size is 10MB.`);
        return false;
      }
      
      return true;
    });

    // Check total file limit
    if (uploadedFiles.length + validFiles.length > 10) {
      showError('Maximum 10 files allowed. Please remove some files before adding new ones.');
      return;
    }

    setUploadedFiles(prev => [...prev, ...validFiles]);
    
    // Clear file input
    event.target.value = '';
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => {
      const newFiles = prev.filter((_, i) => i !== index);
      
      // Auto-untick boolean fields when documents are removed for spousal cover claims
      if (formData.type === '010') {
        const remainingDocumentTypes = newFiles.map(file => file.documentType);
        
        setFormData(prevFormData => {
          const updatedFormData = { ...prevFormData };
          
          // Auto-untick marriageCertificateProvided if no MARRIAGE_CERTIFICATE is uploaded
          if (!remainingDocumentTypes.includes('MARRIAGE_CERTIFICATE')) {
            updatedFormData.marriageCertificateProvided = false;
          }
          
          // Auto-untick spouseRelationshipVerified if no RELATIONSHIP_DECLARATION is uploaded
          if (!remainingDocumentTypes.includes('RELATIONSHIP_DECLARATION')) {
            updatedFormData.spouseRelationshipVerified = false;
          }
          
          return updatedFormData;
        });
      }
      
      // Auto-untick boolean fields when documents are removed for coffin cover claims
      if (formData.type === '012') {
        const remainingDocumentTypes = newFiles.map(file => file.documentType);
        
        setFormData(prevFormData => {
          const updatedFormData = { ...prevFormData };
          
          // Auto-untick funeralReceiptsProvided if no FUNERAL_RECEIPTS is uploaded
          if (!remainingDocumentTypes.includes('FUNERAL_RECEIPTS')) {
            updatedFormData.funeralReceiptsProvided = false;
          }
          
          return updatedFormData;
        });
      }
      
      // Auto-untick boolean fields when documents are removed for parent cover claims
      if (formData.type === '013') {
        const remainingDocumentTypes = newFiles.map(file => file.documentType);
        
        setFormData(prevFormData => {
          const updatedFormData = { ...prevFormData };
          
          // Auto-untick parentRelationshipVerified if no RELATIONSHIP_DECLARATION is uploaded
          if (!remainingDocumentTypes.includes('RELATIONSHIP_DECLARATION')) {
            updatedFormData.parentRelationshipVerified = false;
          }
          
          return updatedFormData;
        });
      }
      
      return newFiles;
    });
  };

  // Submit handler
  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      
      // Final validation - use schema validation if available
      const isValid = schema 
        ? validateStepWithSchema(5)
        : await validateStep(5);
      if (!isValid) {
        return;
      }

      // Additional validation for TTD claims
      if (formData.type === '003' && formData.disabilityType === 'TTD') {
        if (!formData.estimatedPaymentDuration || formData.estimatedPaymentDuration <= 0) {
          toast.error('Please select the number of months for TTD payment (1-6 months)');
          setIsLoading(false);
          return;
        }
      }


      // Convert claim cause descriptions back to codes for backend
      const claimCauseFields = ['causeOfDeath', 'causeOfDisability', 'disabilityCause', 'illnessCause', 'diagnosis'];
      const claimCauseMapping = {};
      let primaryClaimCause = null; // Store the primary claim cause for the claimCause field
      
      for (const field of claimCauseFields) {
        if (formData[field]) {
          try {
            const code = await claimCauseService.getCodeFromDescription(formData[field]);
            claimCauseMapping[field] = code || formData[field]; // Fallback to original if mapping fails
            
            // Set the first valid claim cause as the primary claim cause
            if (!primaryClaimCause && code) {
              primaryClaimCause = code;
            }
          } catch (error) {
            console.warn(`Failed to map claim cause for ${field}:`, error);
            claimCauseMapping[field] = formData[field]; // Fallback to original
          }
        }
      }

      // Prepare form data for submission
      const submissionData = {
        ...formData,
        // Sanitize enum values for backend consistency
        type: sanitizeEnumValue('type', formData.type),
        conditionSeverityStage: formData.conditionSeverityStage 
          ? sanitizeEnumValue('conditionSeverityStage', formData.conditionSeverityStage) 
          : undefined,
        disabilityType: formData.disabilityType 
          ? sanitizeEnumValue('disabilityType', formData.disabilityType) 
          : undefined,
        illnessStage: formData.illnessStage 
          ? sanitizeEnumValue('illnessStage', formData.illnessStage) 
          : undefined,
        // Map claim cause descriptions to codes
        ...claimCauseMapping,
        // Set the primary claim cause for database storage
        claimCause: primaryClaimCause,
        // Cast numeric fields
        sumAssured: formData.sumAssured ? parseFloat(formData.sumAssured) : undefined,
        burialCosts: formData.burialCosts ? parseFloat(formData.burialCosts) : undefined,
        // payoutPercentage: Will be calculated by claims officers during approval
        estimatedPaymentDuration: formData.estimatedPaymentDuration ? parseInt(formData.estimatedPaymentDuration) : null,
        // Convert boolean fields
        isActive: formData.isActive === true,
        employmentStatusConfirmed: formData.employmentStatusConfirmed === true,
        treatmentRequired: formData.treatmentRequired
      };


      // Create FormData for file upload
      const formDataObj = new FormData();
      
      // Add claim data fields directly (not as JSON string)
      Object.keys(submissionData).forEach(key => {
        if (submissionData[key] !== undefined && submissionData[key] !== null) {
          formDataObj.append(key, submissionData[key]);
          if (key === 'type') {
          }
        }
      });
      
      // Add files with document type metadata
      uploadedFiles.forEach((file, index) => {
        formDataObj.append(`documents`, file);
        
        // Add document type as separate field for backend processing
        formDataObj.append(`documentTypes`, file.documentType || 'OTHER');
        
      });


      // Check sumAssured presence before submit (allow 0; only block negative or missing/NaN)
      if (formData.sumAssured === undefined || formData.sumAssured === null || isNaN(parseFloat(formData.sumAssured)) || parseFloat(formData.sumAssured) < 0) {
        setSubmitErrorDialog({
          isOpen: true,
          title: 'Missing Sum Assured',
          message: 'We could not retrieve a valid Sum Assured from SLAMS. Please go back to Step 1 and re-fetch member details, then try again.'
        });
        throw new Error('Missing Sum Assured');
      }

      // Submit the claim
      
      const response = await apiClient.post('claims', formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      

      // Validate successful response: status 201 AND claim object with ID
      const hasValidStatus = response.status === 201;
      const hasValidStructure = !!response.data?.data?.claim?.id;
      
      
      if (hasValidStatus && hasValidStructure) {
        try {
          const serverClaim = response.data?.data?.claim || {};
          const computedBenefit = serverClaim.benefitAmount;
          if (typeof computedBenefit !== 'undefined') {
            setFormData(prev => ({ ...prev, benefitAmount: computedBenefit }));
          }
        } catch (e) {
          console.warn('⚠️ [Frontend] Could not extract server-computed benefit from response');
        }
        showSuccess('Claim submitted successfully! You will receive a confirmation email shortly.');
        setIsSubmitted(true);
        
        // Redirect to claims list after 2 seconds
        setTimeout(() => {
          navigate('/claims');
        }, 2000);
      } else {
        // Handle unexpected response format
        console.error('❌ [Frontend] Validation failed:', {
          status: response.status,
          expectedStatus: 201,
          statusValid: hasValidStatus,
          claimId: response.data?.data?.claim?.id,
          structureValid: hasValidStructure,
          fullResponse: response.data
        });
        setSubmitErrorDialog({
          isOpen: true,
          title: 'Submission Error',
          message: 'We could not complete your submission. Please try again shortly.'
        });
        throw new Error('Invalid response format: claim ID not received');
      }

    } catch (error) {
      console.error('Claim submission error:', error);
      
      if (error.response?.status === 400) {
        const errorData = error.response.data;
        
        // Handle validation details array from backend (new format)
        if (errorData.details && Array.isArray(errorData.details)) {
          setSubmitErrorDialog({
            isOpen: true,
            title: 'Validation Error',
            message: errorData.details.join('. ')
          });
        } 
        // Handle field-specific errors format (legacy format)
        else if (errorData.errors) {
          const newErrors = {};
          errorData.errors.forEach(err => {
            newErrors[err.field] = err.message;
          });
          setErrors(newErrors);
        } 
        // Fallback to main error message
        else {
          setSubmitErrorDialog({
            isOpen: true,
            title: 'Validation Error',
            message: errorData.error || errorData.message || 'Please check your form data and try again.'
          });
        }
      } else if (error.response?.status === 401) {
        showError('Your session has expired. Please log in again.');
        dispatch(logout());
        navigate('/login');
      } else {
        // Handle other HTTP errors with backend error details if available
        const errorData = error.response?.data;
        const specificMessage = errorData?.details && Array.isArray(errorData.details) 
          ? errorData.details.join('. ')
          : errorData?.error || errorData?.message || 'Failed to submit claim. Please try again later.';
          
        setSubmitErrorDialog({
          isOpen: true,
          title: 'Submission Failed',
          message: specificMessage
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isValidPhoneNumber = (phone) => {
    // Backend pattern from UserCreationDTO.js - Ghana phone number format
    const ghanaPhoneRegex = /^(\+233|0)(20|21|23|24|25|26|27|28|29|50|53|54|55|56|57|59)\d{7}$/;
    return ghanaPhoneRegex.test(phone);
  };

  const isValidEmail = (email) => {
    // Backend pattern from UserCreationDTO.js - standard email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const isValidBankAccount = (accountNumber) => {
    // Pattern requirement: 8-20 digits, numbers only
    const bankAccountRegex = /^[0-9]{8,20}$/;
    return bankAccountRegex.test(accountNumber);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Render field based on type with backend-aligned validation
  const renderField = (fieldName, fieldDef, value) => {
    // Guard against undefined field definitions
    if (!fieldDef) {
      console.warn(`Field definition missing for field: ${fieldName}`);
      return (
        <div className="text-red-500 text-sm p-2 border border-red-300 rounded bg-red-50">
          Field definition missing for "{fieldName}". Please check the schema configuration.
        </div>
      );
    }

    const isRequired = isFieldRequired(fieldName);
    // Phase 3: Include validation errors in field styling
    // REMOVED - validationErrors no longer exists after Ghana Card removal
    const hasValidationError = false; // Previously: validationErrors[fieldName]
    const hasFormError = errors[fieldName];
    
    const commonProps = {
      id: fieldName,
      name: fieldName,
      value: value || '',
      onChange: (e) => handleInputChange(fieldName, e.target.value),
      // Phase 3: Add onBlur for validation
      onBlur: (e) => handleFieldBlur(fieldName, e.target.value),
      className: `form-input ${hasFormError ? 'error' : ''} ${hasValidationError ? 'validation-warning' : ''}`,
      disabled: isLoading,
      required: isRequired
    };

    switch (fieldDef.type) {
      case 'string':
        if (fieldName.toLowerCase().includes('address') || fieldName.toLowerCase().includes('details') || fieldName.toLowerCase().includes('cause')) {
          return (
            <textarea
              {...commonProps}
              rows={3}
              className={`form-textarea ${hasFormError ? 'error' : ''} ${hasValidationError ? 'validation-warning' : ''}`}
              placeholder={`Enter ${fieldDef.label?.toLowerCase() || fieldName}...`}
            />
          );
        }
        
        // Add pattern attributes for specific fields
        let additionalProps = {};
        if (fieldName === 'claimantMobile') {
          additionalProps.pattern = "^(\\+233|0)(20|21|23|24|25|26|27|28|29|50|53|54|55|56|57|59)\\d{7}$";
          additionalProps.title = "Include country code (e.g. +233242XXXXXXX)";
        } else if (fieldName === 'accountNumber') {
          additionalProps.pattern = "^[0-9]{8,20}$";
          additionalProps.title = "8–20 digits, numbers only";
        } else if (fieldName === 'claimantEmail') {
          additionalProps.type = "email";
          additionalProps.title = "Must be a valid email, e.g. user@example.com";
        }
        
        return (
          <input
            type={additionalProps.type || "text"}
            placeholder={`Enter ${fieldDef.label?.toLowerCase() || fieldName}...`}
            pattern={additionalProps.pattern}
            title={additionalProps.title}
            {...commonProps}
          />
        );

      case 'number':
        // payoutPercentage field removed - will be handled by claims officers
        
        return (
          <input
            type="number"
            step={fieldName.includes('Cost') || fieldName.includes('Amount') ? "0.01" : "1"}
            min="0"
            placeholder={`Enter ${fieldDef.label?.toLowerCase() || fieldName}...`}
            {...commonProps}
            onChange={(e) => handleInputChange(fieldName, parseFloat(e.target.value) || '')}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            {...commonProps}
          />
        );

      case 'boolean':
        // Special handling for employment status confirmed checkbox
        if (fieldName === 'employmentStatusConfirmed') {
          return (
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                {...commonProps}
                className="form-checkbox"
                checked={value === true}
                onChange={(e) => handleInputChange(fieldName, e.target.checked)}
              />
              <span className="text-sm">Employment Status Confirmed</span>
            </div>
          );
        }
        
        // Special handling for treatment required
        if (fieldName === 'treatmentRequired') {
          return (
            <div className="space-y-2">
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name={fieldName}
                    value="true"
                    checked={value === true}
                    onChange={(e) => handleInputChange(fieldName, true)}
                    className="form-radio"
                  />
                  <span>Yes</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name={fieldName}
                    value="false"
                    checked={value === false}
                    onChange={(e) => handleInputChange(fieldName, false)}
                    className="form-radio"
                  />
                  <span>No</span>
                </label>
              </div>
            </div>
          );
        }
        
        return (
          <select 
            {...commonProps} 
            className={`form-select ${errors[fieldName] ? 'error' : ''}`}
            onChange={(e) => handleInputChange(fieldName, e.target.value === 'true')}
          >
            <option value="">Select...</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        );

      case 'enum':
        return (
          <select {...commonProps} className={`form-select ${errors[fieldName] ? 'error' : ''}`}>
            <option value="">Select {fieldDef.label || fieldName}</option>
            {fieldDef.options?.map(option => (
              <option key={option} value={option}>
                {option.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </option>
            ))}
          </select>
        );

      case 'select':
        return (
          <select {...commonProps} className={`form-select ${errors[fieldName] ? 'error' : ''}`}>
            <option value="">Select {fieldDef.label || fieldName}</option>
            {fieldDef.options?.map(option => (
              <option key={option.value || option} value={option.value || option}>
                {option.label || option.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </option>
            ))}
          </select>
        );

      default:
        return (
          <input
            type="text"
            placeholder={`Enter ${fieldDef.label?.toLowerCase() || fieldName}...`}
            {...commonProps}
          />
        );
    }
  };

  // Render step content
  const renderStepContent = () => {
    // Enhanced schema validation - check for both schema and baseFields
    if (!schema || !schema.baseFields) {
      return (
        <div className="text-center py-12">
          <div className="mb-4">
            <svg className="w-16 h-16 mx-auto text-steel" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <p className="text-steel text-lg">
            {!schema ? 'Schema not loaded yet' : 'Schema structure incomplete'}
          </p>
          <p className="text-steel text-sm mt-2">
            {!schema 
              ? 'Please wait while we load the form schema...' 
              : 'Missing required form field definitions. Please contact technical support.'}
          </p>
        </div>
      );
    }

    // Additional validation for critical fields
    if (!schema.baseFields.policyNumber) {
      console.error('❌ DynamicClaimForm: Critical field policyNumber is missing from schema.baseFields');
      return (
        <div className="text-center py-12">
          <div className="mb-4">
            <svg className="w-16 h-16 mx-auto text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <p className="text-red-700 text-lg font-semibold">Form Configuration Error</p>
          <p className="text-red-600 text-sm mt-2">
            Required field definitions are missing. Please contact technical support.
          </p>
        </div>
      );
    }

    switch (currentStep) {
      case 1:
        
        return (
          <Step1ClaimMetadata
            key="step1"
            formData={formData}
            errors={errors}
            validationErrors={{}} // COMMENTED OUT FOR TESTING - validationErrors
            businessTypes={businessTypes}
            claimCauses={claimCauses}
            isLoading={isLoading}
            onInputChange={handleInputChange}
            onFieldBlur={handleFieldBlur}
            onNext={handleNextStep}
            onPrevious={handlePrevStep}
            currentStep={currentStep}
            schema={schema}
          />
        );

      case 2:
        return (
          <Step2ClaimantInformation
            key="step2"
            formData={formData}
            errors={errors}
            validationErrors={{}} // COMMENTED OUT FOR TESTING - validationErrors
            isLoading={isLoading}
            onInputChange={handleInputChangeWithValidation}
            onFieldBlur={handleFieldBlur}
            onNext={handleNextStep}
            onPrevious={handlePrevStep}
            currentStep={currentStep}
            schema={schema}
          />
        );

      case 3:
        return (
          <Step3ClaimDetails
            key="step3"
            formData={formData}
            errors={errors}
            validationErrors={{}} // COMMENTED OUT FOR TESTING - validationErrors
            visibleFields={visibleFields}
            fieldMetadata={fieldMetadata}
            claimCauses={claimCauses}
            isLoading={isLoading}
            onInputChange={handleInputChangeWithValidation}
            onFieldBlur={handleFieldBlur}
            onNext={handleNextStep}
            onPrevious={handlePrevStep}
            currentStep={currentStep}
            schema={schema}
          />
        );

      case 4:
        return (
          <Step4DocumentUpload
            key="step4"
            formData={formData}
            errors={errors}
            validationErrors={{}} // COMMENTED OUT FOR TESTING - validationErrors
            uploadedFiles={uploadedFiles}
            isLoading={isLoading}
            onInputChange={handleInputChangeWithValidation}
            onFieldBlur={handleFieldBlur}
            onFileUpload={handleFileUploadForStep4}
            onFileRemove={removeFile}
            onNext={handleNextStep}
            onPrevious={handlePrevStep}
            currentStep={currentStep}
            schema={schema}
            showError={showError}
          />
        );

      case 5:
        return (
          <Step5ClaimSummary
            key="step5"
            formData={formData}
            errors={errors}
            isLoading={isLoading}
            onNext={handleSubmit}
            onPrevious={handlePrevStep}
            onEdit={handleEdit}
            uploadedFiles={uploadedFiles}
          />
        );

      default:
        return null;
    }
  };

  // Show loading state
  if (schemaLoading) {
    return (
      <div className="min-h-screen bg-silver-mist py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-12">
            <div className="text-center space-y-6">
              <OmCircularProgress
                indeterminate={true}
                size="large"
              />
              <div className="space-y-2">
                <p className="text-steel font-medium text-lg">Loading claim form...</p>
                <p className="text-steel/70 text-sm">Please wait while we prepare your form</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-silver-mist py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <ConfirmationDialog
          isOpen={validationDialog.isOpen}
          onClose={() => setValidationDialog({ isOpen: false, title: '', message: '', details: [] })}
          type="default"
          title={validationDialog.title}
          message={validationDialog.message}
          confirmText="OK"
          cancelText="Cancel"
          severity="high"
          claimData={{}}
          details={validationDialog.details}
        />
        <ConfirmationDialog
          isOpen={submitErrorDialog.isOpen}
          onClose={() => setSubmitErrorDialog({ isOpen: false, title: '', message: '' })}
          type="default"
          title={submitErrorDialog.title}
          message={submitErrorDialog.message}
          confirmText="OK"
          cancelText="Cancel"
          severity="high"
          claimData={{}}
          details={validationDialog.details}
        />
        
        {/* Back Button */}
        <div className="mb-6">
          <BackButton />
        </div>
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-deep-jungle">Submit New Claim</h1>
          <p className="mt-2 text-steel">
            Complete all steps to submit your claim for processing.
          </p>
        </div>

        {/* Loading Overlay - Show while isLoading is true but not yet submitted */}
        {isLoading && !isSubmitted && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-crystal-white rounded-lg shadow-elevated p-8 max-w-md mx-4 text-center">
              <div className="mb-6 flex justify-center">
                <OmCircularProgress
                  indeterminate={true}
                  size="large"
                />
              </div>
              <h2 className="text-2xl font-bold text-graphite mb-2">
                {currentStep === 5 ? 'Submitting Claim...' : 'Loading...'}
              </h2>
              <p className="text-steel mb-4">
                {currentStep === 5 
                  ? 'Please wait while we process your claim submission.'
                  : 'Please wait while we fetch the required information.'
                }
              </p>
              <p className="text-sm text-steel/70">This may take a few moments</p>
            </div>
          </div>
        )}
        
        {/* Success Overlay */}
        {isSubmitted && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-crystal-white rounded-lg shadow-elevated p-8 max-w-md mx-4 text-center">
              <div className="mb-6">
                <div className="mx-auto w-16 h-16 bg-fresh-leaf rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-crystal-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <h2 className="text-2xl font-bold text-graphite mb-3">Claim Submitted!</h2>
              <p className="text-steel mb-4">
                Your claim has been successfully submitted. You will receive a confirmation email shortly.
              </p>
              <div className="flex items-center justify-center space-x-2 text-sm text-steel/70">
                <OmCircularProgress
                  indeterminate={true}
                  size="small"
                />
                <span>Redirecting to claims list...</span>
              </div>
            </div>
          </div>
        )}

        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {[1,2,3,4,5].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`progress-step ${
                  step < currentStep 
                    ? 'completed' 
                    : step === currentStep 
                    ? 'current'
                    : 'pending'
                }`}>
                  {step < currentStep ? '✓' : step}
                </div>
                {step < 5 && (
                  <div className={`progress-line ${
                    step < currentStep ? 'completed' : 'pending'
                  }`} style={{ minWidth: '40px' }} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2">
            {['Policy Info', 'Personal Details', 'Claim Details', 'Documents', 'Claim Summary'].map(label => (
              <span key={label} className="text-xs text-steel font-medium">{label}</span>
            ))}
          </div>
        </div>

        {/* Error Display */}
        {errors.general && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-red-800">{errors.general}</p>
          </div>
        )}

        {/* Form Content */}
        <div className="form-section">
          <div className="form-section-body">
            {renderStepContent()}
          </div>

          {/* Navigation */}
          <div className="border-t border-gray-200 px-6 py-4">
          </div>
        </div>
      </div>
    </div>
  );
};

// PropTypes for validation
DynamicClaimForm.propTypes = {
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  initialData: PropTypes.object
};

export default DynamicClaimForm;
